import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MoveUpRight, Ruler, Pencil, Square, Circle, Type, Undo2, Eraser, Check, X, RotateCw } from 'lucide-react';
import { rotateDataUrl } from '../lib/model';

const COLORS = ['#ff2d2d', '#ffd60a', '#ffffff', '#1d6fe8', '#111111'];
const TOOLS = [['arrow', MoveUpRight, 'edArrow'], ['measure', Ruler, 'edMeasure'], ['pen', Pencil, 'edPen'], ['rect', Square, 'edRect'], ['circle', Circle, 'edCircle'], ['text', Type, 'edText']];

function label(ctx, txt, x, y, s, color) {
  ctx.font = `bold ${s * 7}px Archivo, Arial`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const w = ctx.measureText(txt).width + s * 4;
  ctx.fillStyle = 'rgba(14,30,48,0.85)';
  ctx.fillRect(x - w / 2, y - s * 5, w, s * 10);
  ctx.fillStyle = color === '#111111' ? '#ffffff' : color;
  ctx.fillText(txt, x, y);
}

function drawShape(ctx, sh) {
  const { a, b, color, s } = sh;
  ctx.strokeStyle = color; ctx.fillStyle = color; ctx.lineWidth = s; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
  const ang = b ? Math.atan2(b[1] - a[1], b[0] - a[0]) : 0;
  ctx.beginPath();
  if (sh.tool === 'pen') {
    sh.pts.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
    ctx.stroke();
  } else if (sh.tool === 'rect') {
    ctx.strokeRect(a[0], a[1], b[0] - a[0], b[1] - a[1]);
  } else if (sh.tool === 'circle') {
    ctx.ellipse((a[0] + b[0]) / 2, (a[1] + b[1]) / 2, Math.abs(b[0] - a[0]) / 2, Math.abs(b[1] - a[1]) / 2, 0, 0, Math.PI * 2);
    ctx.stroke();
  } else if (sh.tool === 'arrow') {
    ctx.moveTo(a[0], a[1]); ctx.lineTo(b[0], b[1]); ctx.stroke();
    const h = s * 4.5;
    ctx.beginPath();
    ctx.moveTo(b[0], b[1]);
    ctx.lineTo(b[0] - h * Math.cos(ang - 0.45), b[1] - h * Math.sin(ang - 0.45));
    ctx.lineTo(b[0] - h * Math.cos(ang + 0.45), b[1] - h * Math.sin(ang + 0.45));
    ctx.closePath(); ctx.fill();
  } else if (sh.tool === 'measure') {
    ctx.moveTo(a[0], a[1]); ctx.lineTo(b[0], b[1]);
    const tk = s * 3, nx = Math.cos(ang + Math.PI / 2) * tk, ny = Math.sin(ang + Math.PI / 2) * tk;
    [a, b].forEach((p) => { ctx.moveTo(p[0] - nx, p[1] - ny); ctx.lineTo(p[0] + nx, p[1] + ny); });
    ctx.stroke();
    if (sh.text) label(ctx, sh.text, (a[0] + b[0]) / 2, (a[1] + b[1]) / 2, s, color);
  } else if (sh.tool === 'text' && sh.text) {
    label(ctx, sh.text, a[0], a[1], s * 1.2, color);
  }
}

export default function PhotoEditor({ t, photo, onSave, onClose }) {
  const cvRef = useRef(null);
  const imgRef = useRef(null);
  const [shapes, setShapes] = useState(photo.annotations || []);
  const [src, setSrc] = useState(photo.original || photo.data);
  const [tool, setTool] = useState('arrow');
  const [color, setColor] = useState(COLORS[0]);
  const [size, setSize] = useState(1);
  const cur = useRef(null);
  const [, force] = useState(0);

  const redraw = () => {
    const cv = cvRef.current, img = imgRef.current;
    if (!cv || !img) return;
    const ctx = cv.getContext('2d');
    ctx.drawImage(img, 0, 0, cv.width, cv.height);
    [...shapes, cur.current].filter(Boolean).forEach((sh) => drawShape(ctx, sh));
  };

  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      imgRef.current = img;
      cvRef.current.width = img.width;
      cvRef.current.height = img.height;
      redraw();
    };
    img.src = src;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);
  const rotate = async () => {
    const H = cvRef.current.height, f = ([x, y]) => [H - y, x];
    const r = await rotateDataUrl(src);
    setShapes((s) => s.map((sh) => ({ ...sh, a: sh.a && f(sh.a), b: sh.b && f(sh.b), pts: sh.pts && sh.pts.map(f) })));
    setSrc(r.data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(redraw, [shapes]);

  const pos = (e) => {
    const r = cvRef.current.getBoundingClientRect();
    return [((e.clientX - r.left) * cvRef.current.width) / r.width, ((e.clientY - r.top) * cvRef.current.height) / r.height];
  };
  const base = () => (Math.max(cvRef.current.width, cvRef.current.height) / 260) * size;

  const down = (e) => {
    e.preventDefault();
    const p = pos(e);
    if (tool === 'text') {
      const txt = window.prompt(t.edPromptText);
      if (txt) setShapes((s) => [...s, { tool, a: p, color, s: base(), text: txt }]);
      return;
    }
    cvRef.current.setPointerCapture?.(e.pointerId);
    cur.current = { tool, a: p, b: p, pts: [p], color, s: base() };
  };
  const move = (e) => {
    if (!cur.current) return;
    const p = pos(e);
    cur.current.b = p;
    if (tool === 'pen') cur.current.pts.push(p);
    redraw();
    force((n) => n + 1);
  };
  const up = () => {
    const sh = cur.current;
    cur.current = null;
    if (!sh) return;
    if (sh.tool !== 'pen' && Math.hypot(sh.b[0] - sh.a[0], sh.b[1] - sh.a[1]) < 4) return redraw();
    if (sh.tool === 'measure') sh.text = window.prompt(t.edPromptMeasure) || '';
    setShapes((s) => [...s, sh]);
  };

  const save = () => {
    cur.current = null;
    redraw();
    onSave({
      data: cvRef.current.toDataURL('image/jpeg', 0.88),
      original: src,
      w: cvRef.current.width,
      h: cvRef.current.height,
      annotations: shapes,
    });
  };

  return createPortal(
    <div className="editor-backdrop" data-testid="photo-editor">
      <div className="editor">
        <div className="editor-head">
          <h4>{t.edTitle}</h4>
          <button className="icon-btn" data-testid="editor-close-btn" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="editor-tools">
          {TOOLS.map(([k, Icon, lbl]) => (
            <button key={k} data-testid={`editor-tool-${k}`} className={`tool ${tool === k ? 'on' : ''}`} onClick={() => setTool(k)}><Icon size={15} /> {t[lbl]}</button>
          ))}
          <span className="tool-sep" />
          {COLORS.map((c) => (
            <button key={c} data-testid={`editor-color-${c.slice(1)}`} className={`swatch ${color === c ? 'on' : ''}`} style={{ background: c }} onClick={() => setColor(c)} aria-label={c} />
          ))}
          <span className="tool-sep" />
          {[0.6, 1, 1.6].map((v, i) => (
            <button key={v} data-testid={`editor-size-${i}`} className={`tool size ${size === v ? 'on' : ''}`} onClick={() => setSize(v)}><span style={{ width: 4 + i * 4, height: 4 + i * 4 }} /></button>
          ))}
          <span className="tool-sep" />
          <button data-testid="editor-rotate-btn" className="tool" onClick={rotate} title={t.phRotImg}><RotateCw size={15} /> 90°</button>
        </div>
        <div className="editor-canvas-wrap">
          <canvas ref={cvRef} data-testid="editor-canvas" onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up} />
        </div>
        <div className="row">
          <button className="btn btn-outline" data-testid="editor-undo-btn" disabled={!shapes.length} onClick={() => setShapes((s) => s.slice(0, -1))}><Undo2 size={16} /> {t.edUndo}</button>
          <button className="btn btn-outline" data-testid="editor-clear-btn" disabled={!shapes.length} onClick={() => setShapes([])}><Eraser size={16} /> {t.edClear}</button>
          <button className="btn btn-outline" data-testid="editor-cancel-btn" onClick={onClose}><X size={16} /> {t.edCancel}</button>
          <button className="btn btn-green" data-testid="editor-save-btn" onClick={save}><Check size={16} /> {t.edSave}</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
